
import { useState } from 'react';
import { uploadFile, deleteFile, listBuckets, ensureBucketForUpload } from '@/utils/storage';
import { FileUploadService } from '@/services/FileUploadService';
import { toast } from 'sonner';

interface FileUploaderProps {
  initialLogoUrl?: string;
  initialPitchDeckUrl?: string;
  initialWebsitePreviewUrl?: string;
  onLogoUpload?: (url: string) => void;
  onPitchDeckUpload?: (url: string) => void;
  onWebsitePreviewUpload?: (url: string) => void;
}

export const useFileUploader = ({
  initialLogoUrl = '',
  initialPitchDeckUrl = '',
  initialWebsitePreviewUrl = '',
  onLogoUpload,
  onPitchDeckUpload,
  onWebsitePreviewUpload
}: FileUploaderProps) => {
  const [isLogoUploading, setIsLogoUploading] = useState(false);
  const [isPitchDeckUploading, setIsPitchDeckUploading] = useState(false);
  const [isPreviewUploading, setIsPreviewUploading] = useState(false);
  const [previewExpanded, setPreviewExpanded] = useState(false);
  const [uploadErrors, setUploadErrors] = useState<Record<string, string>>({
    logo: '',
    pitch_deck: '',
    website_preview: ''
  });
  
  // Check available buckets on component mount
  const [bucketInfo, setBucketInfo] = useState({
    checked: false,
    availableBuckets: [] as string[],
    selectedBucket: ''
  });
  
  const checkBuckets = async () => {
    try {
      if (!bucketInfo.checked) {
        const buckets = await listBuckets();
        console.log('Available buckets:', buckets);
        
        setBucketInfo({
          checked: true,
          availableBuckets: buckets,
          selectedBucket: buckets.includes('public-images') ? 'public-images' : 
                         (buckets.length > 0 ? buckets[0] : '')
        });
        
        return buckets.includes('public-images') ? 'public-images' : 
              (buckets.length > 0 ? buckets[0] : '');
      }
      
      return bucketInfo.selectedBucket || '';
    } catch (error) {
      console.error('Error checking buckets:', error);
      setBucketInfo(prev => ({
        ...prev,
        checked: true,
        availableBuckets: []
      }));
      return '';
    }
  };

  const validateFile = (file: File, type: 'logo' | 'pitch_deck' | 'website_preview'): boolean => {
    // Reset error for this file type
    setUploadErrors(prev => ({ ...prev, [type]: '' }));
    
    // Check file size (5MB for images, 50MB for documents)
    const maxSize = type === 'pitch_deck' ? 50 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > maxSize) {
      const errorMsg = `File too large. Maximum size is ${type === 'pitch_deck' ? '50MB' : '5MB'}.`;
      setUploadErrors(prev => ({ ...prev, [type]: errorMsg }));
      toast.error(errorMsg);
      return false;
    }
    
    // Validate file type
    if (type === 'logo' || type === 'website_preview') {
      // For images
      const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!validImageTypes.includes(file.type)) {
        const errorMsg = 'Invalid file type. Please upload an image (JPEG, PNG, GIF, WebP).';
        setUploadErrors(prev => ({ ...prev, [type]: errorMsg }));
        toast.error(errorMsg);
        return false;
      }
    } else if (type === 'pitch_deck') {
      // For documents
      const validDocTypes = ['application/pdf', 'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation'];
      if (!validDocTypes.includes(file.type)) {
        const errorMsg = 'Invalid file type. Please upload a PDF or PowerPoint document.';
        setUploadErrors(prev => ({ ...prev, [type]: errorMsg }));
        toast.error(errorMsg);
        return false;
      }
    }
    
    return true;
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, type: 'logo' | 'pitch_deck' | 'website_preview') => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!validateFile(file, type)) {
      return;
    }

    let setIsUploading: React.Dispatch<React.SetStateAction<boolean>>;
    let onUploadComplete: ((url: string) => void) | undefined;
    let fileCategory: string;
    
    if (type === 'logo') {
      setIsUploading = setIsLogoUploading;
      onUploadComplete = onLogoUpload;
      fileCategory = 'logos';
    } else if (type === 'pitch_deck') {
      setIsUploading = setIsPitchDeckUploading;
      onUploadComplete = onPitchDeckUpload;
      fileCategory = 'pitch-decks';
    } else {
      setIsUploading = setIsPreviewUploading;
      onUploadComplete = onWebsitePreviewUpload;
      fileCategory = 'website-previews';
    }
    
    setIsUploading(true);
    setUploadErrors(prev => ({ ...prev, [type]: '' }));
    
    try {
      // Check buckets first
      await checkBuckets();
      
      // Create a clean filename
      const fileExt = file.name.split('.').pop() || '';
      const cleanName = file.name.replace(/\.[^/.]+$/, "").replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
      const fileName = `${fileCategory}/${cleanName}-${Date.now()}.${fileExt}`;
      
      // Use FileUploadService which now has local storage fallback
      const result = await FileUploadService.uploadFile(file, {
        path: fileName,
        fileType: type === 'pitch_deck' ? 'document' : 'image',
        maxSizeInMB: type === 'pitch_deck' ? 50 : 5,
        allowedTypes: [],
        useLocalFallback: true
      });
      
      if (result) {
        if (onUploadComplete) {
          onUploadComplete(result.url);
        }
        
        if (result.isLocal) {
          toast.success(`${type.replace('_', ' ')} saved locally`);
        } else {
          toast.success(`${type.replace('_', ' ')} uploaded successfully`);
        }
      } else {
        throw new Error('Failed to upload or save locally');
      }
    } catch (error) {
      console.error(`Error handling ${type}:`, error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setUploadErrors(prev => ({ ...prev, [type]: errorMessage }));
      toast.error(`Failed to handle ${type}: ${errorMessage}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleExternalUrl = (type: 'logo' | 'pitch_deck' | 'website_preview', url: string) => {
    let onUploadComplete: ((url: string) => void) | undefined;
    
    if (type === 'logo') {
      onUploadComplete = onLogoUpload;
    } else if (type === 'pitch_deck') {
      onUploadComplete = onPitchDeckUpload;
    } else {
      onUploadComplete = onWebsitePreviewUpload;
    }
    
    try {
      // Validate URL
      new URL(url);
      
      if (onUploadComplete) {
        onUploadComplete(url);
        toast.success(`External ${type.replace('_', ' ')} URL set successfully`);
      }
    } catch (error) {
      setUploadErrors(prev => ({ ...prev, [type]: 'Invalid URL format' }));
      toast.error('Invalid URL format. Please enter a valid URL starting with http:// or https://');
    }
  };

  const handleRemoveFile = async (type: 'logo' | 'pitch_deck' | 'website_preview') => {
    let currentUrl = '';
    let onUploadComplete: ((url: string) => void) | undefined;
    
    if (type === 'logo') {
      currentUrl = initialLogoUrl;
      onUploadComplete = onLogoUpload;
    } else if (type === 'pitch_deck') {
      currentUrl = initialPitchDeckUrl;
      onUploadComplete = onPitchDeckUpload;
    } else {
      currentUrl = initialWebsitePreviewUrl;
      onUploadComplete = onWebsitePreviewUpload;
    }
    
    if (!currentUrl) return;
    
    // Handle different types of URLs
    if (currentUrl.startsWith('data:') || currentUrl.startsWith('blob:')) {
      // Just clear the reference for data/blob URLs
      if (onUploadComplete) {
        onUploadComplete('');
        toast.success(`${type.replace('_', ' ')} removed`);
      }
      return;
    }
    
    // Try to delete file from storage
    try {
      // Check if it's a Supabase URL
      if (currentUrl.includes('supabase') || 
          currentUrl.includes('company-assets') || 
          currentUrl.includes('business-files') ||
          currentUrl.includes('public-images')) {
        
        // Extract bucket and path from URL
        const urlObj = new URL(currentUrl);
        const pathParts = urlObj.pathname.split('/');
        const bucketIndex = pathParts.findIndex(part => 
          ['company-assets', 'business-files', 'documents', 'companies', 'public-images'].includes(part)
        );
        
        if (bucketIndex !== -1) {
          const bucket = pathParts[bucketIndex];
          const path = pathParts.slice(bucketIndex + 1).join('/');
          
          console.log(`Removing file from bucket: ${bucket}, path: ${path}`);
          
          const success = await deleteFile(path, bucket);
          
          if (success) {
            if (onUploadComplete) {
              onUploadComplete('');
            }
            toast.success(`${type.replace('_', ' ')} removed successfully`);
            return;
          }
        }
      }
      
      // For any other case (including local storage or if Supabase deletion fails)
      // Just clear the reference
      if (onUploadComplete) {
        onUploadComplete('');
        toast.success(`${type.replace('_', ' ')} reference removed`);
      }
    } catch (error) {
      console.error(`Error removing ${type}:`, error);
      // Still clear the URL even if deletion fails
      if (onUploadComplete) {
        onUploadComplete('');
        toast.success(`${type.replace('_', ' ')} reference removed`);
      }
    }
  };
  
  return {
    isLogoUploading,
    isPitchDeckUploading,
    isPreviewUploading,
    previewExpanded,
    uploadErrors,
    setPreviewExpanded,
    handleFileUpload,
    handleRemoveFile,
    handleExternalUrl,
    bucketInfo
  };
};
